import React, { useEffect, useRef, useState } from "react";
import { Dialog, Button, Typography, Box } from "@mui/material";
import { getMe, logout } from "../utils/auth";
import { useLocation, useNavigate } from "react-router-dom";
import useIdleLogout from "../hooks/useIdleLogout";
import useAbsoluteSessionMax from "../hooks/useAbsoluteSessionMax";
import styles from "./RequireAuth.styles";

const IDLE_MS = 30 * 60 * 1000;
const WARN_MS = 60 * 1000;
const ABS_MAX_MS = 8 * 60 * 60 * 1000;
const SESSION_TS_KEY = "sessionStartMs";
const DEPLOYMENT_ID_KEY = "appDeploymentId";
const DEPLOYMENT_CHECK_MS = 15 * 1000;

function getCurrentDeploymentId() {
  return window.__ENV__?.deploymentId || "unknown";
}

function shouldLogoutForDeploymentChange(nextDeploymentId = getCurrentDeploymentId()) {
  let storedDeploymentId = null;

  try {
    storedDeploymentId = localStorage.getItem(DEPLOYMENT_ID_KEY);
  } catch {
    return false;
  }

  if (storedDeploymentId && storedDeploymentId !== nextDeploymentId) {
    return true;
  }

  try {
    localStorage.setItem(DEPLOYMENT_ID_KEY, nextDeploymentId);
  } catch {}
  return false;
}

export default function RequireAuth({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);

  const [showWarn, setShowWarn] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const countdownTimer = useRef(null);

  const clearCountdown = () => {
    if (countdownTimer.current) window.clearInterval(countdownTimer.current);
    countdownTimer.current = null;
  };

  const startWarningWindow = () => {
    if (showWarn) return;
    setShowWarn(true);
    const deadline = Date.now() + WARN_MS;
    setSecondsLeft(Math.ceil((deadline - Date.now()) / 1000));
    clearCountdown();
    countdownTimer.current = window.setInterval(() => {
      const remaining = Math.max(0, deadline - Date.now());
      setSecondsLeft(Math.ceil(remaining / 1000));
      if (remaining <= 0) {
        clearCountdown();
        setShowWarn(false);
        logout();
      }
    }, 250);
  };

  const onIdleLogout = () => {
    clearCountdown();
    setShowWarn(false);
    logout();
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await getMe();
        if (cancelled) return;

        if (me && me.user && me.user.email) {
          if (shouldLogoutForDeploymentChange()) {
            logout();
            return;
          }

          if (!localStorage.getItem(SESSION_TS_KEY)) {
            try {
              localStorage.setItem(SESSION_TS_KEY, String(Date.now()));
            } catch {}
          }
          setSignedIn(true);
          setLoading(false);
        } else {
          setSignedIn(false);
          setLoading(false);
          navigate("/signed-out", { replace: true });
        }
      } catch {
        if (!cancelled) {
          setSignedIn(false);
          setLoading(false);
          navigate("/signed-out", { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // re-run the auth check on every route change
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!signedIn) return;

    let cancelled = false;

    const checkDeployment = async () => {
      try {
        const res = await fetch(`/deployment.json?t=${Date.now()}`, {
          cache: "no-store",
        });

        if (!res.ok || cancelled) return;

        const deployment = await res.json();
        const deploymentId = deployment && deployment.deploymentId;

        if (
          deploymentId &&
          shouldLogoutForDeploymentChange(String(deploymentId))
        ) {
          logout();
        }
      } catch {}
    };

    const deploymentTimer = window.setInterval(
      checkDeployment,
      DEPLOYMENT_CHECK_MS
    );

    return () => {
      cancelled = true;
      window.clearInterval(deploymentTimer);
    };
  }, [signedIn]);

  const { reset } = useIdleLogout({
    enabled: !loading && signedIn,
    idleMs: IDLE_MS,
    warnMs: WARN_MS,
    onWarn: startWarningWindow,
    onLogout: onIdleLogout,
  });

  useEffect(() => {
    if (!showWarn) return;
    const onIntent = () => {
      setShowWarn(false);
      clearCountdown();
      if (typeof reset === "function") reset();
    };
    const EVENTS = ["click", "keydown", "touchstart"];
    EVENTS.forEach((ev) =>
      window.addEventListener(ev, onIntent, { passive: true })
    );
    return () =>
      EVENTS.forEach((ev) =>
        window.removeEventListener(ev, onIntent)
      );
  }, [showWarn, reset]);

  const staySignedIn = () => {
    setShowWarn(false);
    clearCountdown();
    if (typeof reset === "function") reset();
  };

  useAbsoluteSessionMax({
    enabled: !loading && signedIn,
    maxMs: ABS_MAX_MS,
    onLogout: () => {
      clearCountdown();
      setShowWarn(false);
      logout();
    },
    tsKey: SESSION_TS_KEY,
  });

  useEffect(() => {
    if (!signedIn) clearCountdown();
    return () => clearCountdown();
  }, [signedIn]);

  if (loading || !signedIn) return null;

  return (
    <>
      {children}

      <Dialog
        open={showWarn}
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
        keepMounted
        BackdropProps={{ sx: styles.dialogBackdrop }}
        PaperProps={{ elevation: 8, sx: styles.dialogPaper }}
      >
        <Box sx={styles.container}>
          <Typography variant="h6" component="div" sx={styles.title}>
            Session about to expire
          </Typography>
          <Typography variant="body2" component="div" sx={styles.body}>
            You’ll be signed out soon due to inactivity.
          </Typography>

          {typeof secondsLeft === "number" && (
            <Box component="span" sx={styles.countdownPill}>
              ~{secondsLeft}s
            </Box>
          )}

          <Box sx={styles.actionsRow}>
            <Button
              variant="contained"
              onClick={() => logout()}
              sx={styles.btnGrey}
            >
              Sign out now
            </Button>
            <Button
              variant="contained"
              onClick={staySignedIn}
              sx={styles.btnPurple}
              autoFocus
            >
              Stay signed in
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
